import { ColumnFilter, ColumnFiltersState, Table } from '@tanstack/table-core'
import { format } from 'date-fns'
import { es } from 'date-fns/locale/es'
import { DateRange } from 'react-day-picker'
import { useState } from 'react'

import { Cross1Icon } from '@radix-ui/react-icons'
import { Badge } from './Badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Column } from '@tanstack/react-table'
import { ManageFilterOptionsProps } from './table.types'

interface ManageFilterProps<TData> extends ManageFilterOptionsProps {
  columnFilters: ColumnFiltersState
  table: Table<TData>
}

export function ManageFilters<TData>({
  columnFilters,
  columnLabel,
  dateFilterColumns = [],
  rangeFilterColumns = [],
  searchFilterColumns = [],
  selectOptions = {},
  selectSearchFilterColumns = [],
  table,
}: ManageFilterProps<TData>) {
  const [open, setOpen] = useState(false)

  const cleanFilters = () => {
    table.setColumnFilters([])
    setOpen(false)
  }

  const handleFilter = (
    filterType: string,
    column: Column<TData, unknown> | undefined,
    values: string[],
    value: string
  ) => {
    switch (filterType) {
      case 'clearAll':
        column?.setFilterValue([])
        break
      case 'clear':
        column?.setFilterValue(undefined)
        break
      case 'remove':
        // eslint-disable-next-line no-case-declarations
        const filteredValues = values.filter((v: string) => v !== value)
        if (filteredValues.length === 0) {
          column?.setFilterValue(undefined)
          break
        }
        column?.setFilterValue(filteredValues)
        break
      default:
        break
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {columnFilters.length > 0 && (
        <>
          <PopoverTrigger className="text-sm font-bold underline">Filtros aplicados</PopoverTrigger>
          <PopoverContent className="flex h-full w-[400px] flex-col gap-2 bg-white" align="end">
            <div
              className="cursor-pointer text-sm text-gray-700 underline hover:text-black"
              onClick={cleanFilters}
            >
              Borrar todo
            </div>
            <div className="flex max-h-72 flex-wrap gap-2 overflow-auto">
              {columnFilters.map((filter: ColumnFilter) => {
                const values = filter.value as string[]
                const column = table.getColumn(filter.id)
                if (rangeFilterColumns.includes(filter.id)) {
                  return (
                    <Badge key={filter.id} variant="filter">
                      <Cross1Icon
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => handleFilter('clear', column, values, values[0])}
                      />
                      <div>
                        {columnLabel[filter.id as keyof typeof columnLabel]}: {values[0]} -{' '}
                        {values[1]}
                      </div>
                    </Badge>
                  )
                }
                if (dateFilterColumns.includes(filter.id)) {
                  const dateValues = filter.value as DateRange
                  return (
                    dateValues?.from && (
                      <Badge key={filter.id} variant="filter">
                        <Cross1Icon
                          className="h-3 w-3 cursor-pointer"
                          onClick={() => handleFilter('clear', column, values, values[0])}
                        />
                        <div>
                          {columnLabel[filter.id as keyof typeof columnLabel]}:{' '}
                          {dateValues?.from &&
                            (dateValues.to ? (
                              <>
                                {format(dateValues.from, 'd MMM y', { locale: es })} -{' '}
                                {format(dateValues.to, 'd MMM y', { locale: es })}
                              </>
                            ) : (
                              format(dateValues.from, 'd MMM y', { locale: es })
                            ))}
                        </div>
                      </Badge>
                    )
                  )
                }
                if (selectSearchFilterColumns.includes(filter.id)) {
                  const columnOptions = selectOptions[filter.id] || []
                  return values?.map((value: string) => {
                    const option = columnOptions.find((o) => o.value === value)
                    return (
                      <Badge key={`${filter.id}-${value}`} variant="filter">
                        <Cross1Icon
                          className="h-3 w-3 cursor-pointer"
                          onClick={() => handleFilter('remove', column, values, value)}
                        />
                        <div className="font-light">
                          {columnLabel[filter.id as keyof typeof columnLabel]}:{' '}
                          {option?.label || value}
                        </div>
                      </Badge>
                    )
                  })
                }
                if (searchFilterColumns.includes(filter.id)) {
                  const value = filter.value as string
                  return (
                    <Badge key={filter.id} variant="filter">
                      <Cross1Icon
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => handleFilter('clear', column, values, value)}
                      />
                      <div className="font-light">
                        {columnLabel[filter.id as keyof typeof columnLabel]}: {value}
                      </div>
                    </Badge>
                  )
                }
                return null
              })}
            </div>
          </PopoverContent>
        </>
      )}
    </Popover>
  )
}
